/**
 * BonatUploadCard.jsx — v-bonat 2026-06
 *
 * رفع تقرير بونات (xlsx) لإثراء ملف العميل بالجنس/الميلاد/الإيميل.
 * إثراء إضافي لا يمسّ بيانات رتم. تظهر فقط لمن يملك customers.bonat_upload.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

export default function BonatUploadCard({ perms }) {
  const [myPerms, setMyPerms] = useState(perms || null);
  useEffect(() => {
    if (myPerms) return;
    api.get("/auth/me/permissions")
      .then((r) => setMyPerms(r.data?.permissions || []))
      .catch(() => setMyPerms([]));
  }, []);  // eslint-disable-line
  const canUpload = (myPerms || []).includes("customers.bonat_upload");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const loadStatus = () => {
    api.get("/customers/bonat/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  };
  useEffect(() => { if (canUpload) loadStatus(); }, [canUpload]);

  if (!canUpload) return null;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr(null); setReport(null); setProgress({ processed: 0, total: null });
    try {
      const fd = new FormData();
      fd.append("file", f);
      // رد مبكر بمعرّف عملية — لا ينتظر اكتمال المعالجة (يتجاوز مهلة 20s).
      const start = await api.post("/customers/bonat/upload", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("لم تبدأ عملية الرفع");
      // استطلاع الحالة حتى الاكتمال — التقرير يظهر عند done لا عند البدء.
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 1500));
        const s = await api.get(`/customers/bonat/upload-status/${jobId}`);
        const d = s.data || {};
        setProgress({ processed: d.processed || 0, total: d.total });
        if (d.status === "done") { setReport(d.report); loadStatus(); setProgress(null); return; }
        if (d.status === "error") { setErr(d.error || "تعذّر رفع الملف"); setProgress(null); return; }
      }
      setErr("طالت المعالجة أكثر من المتوقّع — تحقّق لاحقًا، فقد تكون اكتملت.");
      setProgress(null);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "تعذّر رفع الملف");
      setProgress(null);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const Row = ({ k, v, hi }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0",
                  borderBottom: "1px dashed rgba(61,42,28,0.10)" }}>
      <span style={{ color: "#57534E", fontSize: 13 }}>{k}</span>
      <b style={{ color: hi ? "#B45309" : "#3D2A1C", fontSize: 13 }}>{v}</b>
    </div>
  );

  return (
    <section data-testid="bonat-upload-card" style={{
      marginBottom: 24, padding: 20, borderRadius: 16, background: "#FFFFFF",
      border: "2px solid rgba(124,58,237,0.25)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#5B21B6" }}>
            ⬆️ رفع تقرير بونات (إثراء العملاء)
          </h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "#57534E", lineHeight: 1.7 }}>
            يضيف الجنس · تاريخ الميلاد · الإيميل فقط. إثراء إضافي — لا يمسّ اسم/شراء رتم،
            والحقل الفارغ لا يمحو قيمة قائمة. رفع نفس الملف مرتين لا يُكرّر.
          </p>
        </div>
        <label data-testid="bonat-upload-trigger" style={{
          cursor: busy ? "wait" : "pointer", background: "#7C3AED", color: "white",
          padding: "10px 18px", borderRadius: 10, fontWeight: 700, fontSize: 14,
          opacity: busy ? 0.7 : 1, whiteSpace: "nowrap",
        }}>
          {busy ? "جارٍ المعالجة…" : "اختر ملف xlsx"}
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onFile}
                 disabled={busy} style={{ display: "none" }} data-testid="bonat-file-input" />
        </label>
      </div>

      {progress && (
        <div data-testid="bonat-progress" style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, color: "#5B21B6", fontWeight: 700, marginBottom: 6 }}>
            ⏳ جارٍ المعالجة… {progress.total
              ? `${(progress.processed || 0).toLocaleString("ar")} / ${(progress.total).toLocaleString("ar")} صف`
              : "يقرأ الملف ويحضّر السجلات"}
          </div>
          <div style={{ height: 8, borderRadius: 8, background: "#EDE9FE", overflow: "hidden" }}>
            <div style={{
              height: "100%",
              width: progress.total ? `${Math.round((progress.processed / progress.total) * 100)}%` : "12%",
              background: "#7C3AED", transition: "width 300ms ease",
            }} />
          </div>
        </div>
      )}

      {status && (
        <div data-testid="bonat-status" style={{ marginTop: 12, fontSize: 12, color: "#57534E" }}>
          المُثرَون حاليًا: <b>{(status.enriched_total || 0).toLocaleString("ar")}</b>
          {" · "}بجنس: <b>{(status.with_gender || 0).toLocaleString("ar")}</b>
          {" · "}بميلاد: <b>{(status.with_birthday || 0).toLocaleString("ar")}</b>
          {" · "}قاصرون معلَّمون: <b>{(status.minors_flagged || 0).toLocaleString("ar")}</b>
        </div>
      )}

      {err && (
        <div data-testid="bonat-error" style={{ marginTop: 12, padding: 10, borderRadius: 8,
             background: "#FEF3F2", color: "#B91C1C", fontSize: 13, fontWeight: 600 }}>
          {err}
        </div>
      )}

      {report && (
        <div data-testid="bonat-report" style={{ marginTop: 16, padding: 14, borderRadius: 12,
             background: "#F5F3FF", border: "1px solid rgba(124,58,237,0.20)" }}>
          <div style={{ fontWeight: 800, color: "#5B21B6", marginBottom: 8 }}>تقرير الرفع</div>
          <Row k="قُرئ من الملف" v={(report.read || 0).toLocaleString("ar")} />
          <Row k="أرقام صالحة" v={(report.valid_phone || 0).toLocaleString("ar")} />
          <Row k="طابق سجلات رتم" v={(report.matched_retm || 0).toLocaleString("ar")} hi />
          <Row k="لم يطابق رتم" v={(report.unmatched_retm || 0).toLocaleString("ar")} />
          <Row k="أرقام مرفوضة" v={(report.rejected_phone || 0).toLocaleString("ar")} />
          <Row k="مكرّر داخل الملف" v={(report.duplicates_in_file || 0).toLocaleString("ar")} />
          <div style={{ marginTop: 10, fontWeight: 700, color: "#5B21B6" }}>أُضيف لكل حقل:</div>
          <Row k="الجنس" v={(report.added?.gender || 0).toLocaleString("ar")} />
          <Row k="الميلاد" v={(report.added?.birthday || 0).toLocaleString("ar")} />
          <Row k="الإيميل" v={(report.added?.email || 0).toLocaleString("ar")} />
          <div style={{ marginTop: 10, fontWeight: 700, color: "#B91C1C" }}>مواليد مرفوضة (كل قاعدة):</div>
          {Object.entries(report.birthday_rejected_by_rule || {}).map(([k, v]) => (
            <Row key={k} k={k} v={(v || 0).toLocaleString("ar")} />
          ))}
          <div style={{ marginTop: 10, fontWeight: 700, color: "#5B21B6" }}>قاصرون معلَّمون (يُستبعدون من الحملات):</div>
          <Row k="الإجمالي" v={(report.minors_flagged?.total || 0).toLocaleString("ar")} hi />
          <Row k="دون 13" v={(report.minors_flagged?.under_13 || 0).toLocaleString("ar")} />
          <Row k="13–17" v={(report.minors_flagged?.["13_to_17"] || 0).toLocaleString("ar")} />
        </div>
      )}
    </section>
  );
}
